import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Users, MessageCircle, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { customers as api } from "@/lib/api";
import { formatDate, formatIDR, statusColor, statusLabel, platformLabel, tierColor } from "@/lib/format";

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", whatsapp_number: "", email: "", source_platform: "shopee", last_product_bought: "" });

  const fetchList = async () => {
    setLoading(true);
    try {
      const data = await api.list({ search, status_filter: statusFilter, source: sourceFilter });
      setRows(data);
    } catch (err) { console.warn("Customers load failed:", err); }
    setLoading(false);
  };

  useEffect(() => { fetchList(); }, []);

  const doSearch = (e) => { e.preventDefault(); fetchList(); };

  const create = async () => {
    if (!form.name || !form.whatsapp_number) { toast.error("Nama & WhatsApp wajib"); return; }
    try {
      await api.create(form);
      toast.success("Customer ditambahkan");
      setDialogOpen(false);
      setForm({ name: "", whatsapp_number: "", email: "", source_platform: "shopee", last_product_bought: "" });
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus customer ini?")) return;
    await api.remove(id);
    toast.success("Customer dihapus");
    fetchList();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Customer Database</h1>
          <p className="text-sm text-gray-500 mt-1">Semua pelanggan Anda dalam satu tempat. Milik brand Anda.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-customer-btn">
              <Plus className="mr-2 h-4 w-4" /> Tambah Customer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Tambah Customer Manual</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-customer-name" /></div>
              <div><Label>WhatsApp</Label><Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="08..." data-testid="new-customer-wa" /></div>
              <div><Label>Email (opsional)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-customer-email" /></div>
              <div>
                <Label>Marketplace Asal</Label>
                <Select value={form.source_platform} onValueChange={(v) => setForm({ ...form, source_platform: v })}>
                  <SelectTrigger data-testid="new-customer-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shopee">Shopee</SelectItem>
                    <SelectItem value="tiktok">TikTok Shop</SelectItem>
                    <SelectItem value="tokopedia">Tokopedia</SelectItem>
                    <SelectItem value="lazada">Lazada</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Produk Terakhir</Label><Input value={form.last_product_bought} onChange={(e) => setForm({ ...form, last_product_bought: e.target.value })} data-testid="new-customer-product" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-customer-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-gray-200">
        <CardContent className="p-4">
          <form onSubmit={doSearch} className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input placeholder="Cari nama, WhatsApp, atau produk..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-input" />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); }}>
              <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="new">Baru</SelectItem>
                <SelectItem value="active">Aktif</SelectItem>
                <SelectItem value="repeat">Repeat</SelectItem>
                <SelectItem value="loyal">Loyal</SelectItem>
                <SelectItem value="at_risk">Berisiko</SelectItem>
                <SelectItem value="inactive">Non-aktif</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); }}>
              <SelectTrigger className="w-44" data-testid="filter-source"><SelectValue placeholder="Marketplace" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Marketplace</SelectItem>
                <SelectItem value="shopee">Shopee</SelectItem>
                <SelectItem value="tiktok">TikTok Shop</SelectItem>
                <SelectItem value="tokopedia">Tokopedia</SelectItem>
                <SelectItem value="lazada">Lazada</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="outline" data-testid="apply-filter-btn">Terapkan</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="mx-auto text-gray-300" size={56} />
            <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada customer</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              Buat QR Loyalty pertama Anda dan masukkan ke paket marketplace. Setiap pembeli yang scan akan otomatis masuk ke sini.
            </p>
            <Link to="/dashboard/qr-loyalty"><Button className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">Buat QR Loyalty</Button></Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Pelanggan</th>
                  <th className="px-4 py-3">Marketplace</th>
                  <th className="px-4 py-3">Produk Terakhir</th>
                  <th className="px-4 py-3">Total Order</th>
                  <th className="px-4 py-3">Total Spending</th>
                  <th className="px-4 py-3">Tier</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Last Order</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50" data-testid={`customer-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/dashboard/customers/${c.id}`} className="font-semibold text-gray-900 hover:text-emerald-600">{c.name}</Link>
                      <div className="text-xs text-gray-500">{c.whatsapp_number}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{platformLabel(c.source_platform)}</td>
                    <td className="px-4 py-3 text-gray-700">{c.last_product_bought || "-"}</td>
                    <td className="px-4 py-3 text-gray-900 font-semibold">{c.total_order || 0}</td>
                    <td className="px-4 py-3 text-gray-900 font-semibold">{formatIDR(c.total_spending)}</td>
                    <td className="px-4 py-3"><Badge className={`${tierColor(c.membership_tier)} capitalize border`}>{c.membership_tier}</Badge></td>
                    <td className="px-4 py-3"><Badge className={`${statusColor(c.customer_status)} border`}>{statusLabel(c.customer_status)}</Badge></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(c.last_order_date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <a href={`https://wa.me/${(c.whatsapp_number || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" title="Chat WhatsApp">
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`wa-${c.id}`}><MessageCircle size={16} className="text-green-600" /></Button>
                        </a>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(c.id)} data-testid={`delete-${c.id}`}><Trash2 size={16} className="text-red-500" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
